/** Access token is refreshed this long before its own exp. The login itself lasts for the Cognito refresh token. */
export const REFRESH_BEFORE_MS = 10 * 60 * 1000;
/** When a refresh is already due, wait briefly instead of calling in a loop. */
export const MIN_RETRY_DELAY_MS = 15 * 1000;
/** A failed refresh must not run again immediately. */
export const MIN_REFRESH_INTERVAL_MS = 60 * 1000;

const TRANSIENT_RETRY_DELAYS_MS = [60 * 1000, 5 * 60 * 1000];
const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);
const GATEWAY_STATUSES = new Set([502, 503, 504]);
const MAX_NETWORK_RETRIES = 2;

const COGNITO_AUTH_CODES = new Set([
  "NotAuthorizedException",
  "NotAuthorized",
  "PasswordResetRequiredException",
  "UserNotFoundException",
]);

const authRejectedListeners = new Set();

/**
 * Cognito rejected the refresh token (expired, revoked, or an invalid session).
 * Network and HTTP failures are not auth rejections.
 */
export function isCognitoAuthRejection(error) {
  if (!error) return false;
  if (typeof error === "string") {
    return /invalid session|refresh token has expired|invalid refresh token/i.test(
      error
    );
  }
  const code = error.code || error.name;
  if (COGNITO_AUTH_CODES.has(code)) return true;
  const message = String(error.message || "");
  return /refresh token has expired|invalid refresh token|invalid session/i.test(
    message
  );
}

export function apiErrorMessage(error) {
  if (!error) return "Request failed";
  if (typeof error === "string" && error.trim()) return error.trim();

  const data = error.response?.data ?? error.data;
  if (typeof data === "string" && data.trim() && !isHtml(data)) return data.trim();
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  if (typeof data?.error === "string" && data.error.trim()) {
    return data.error.trim();
  }
  if (typeof error.message === "string" && error.message.trim()) {
    if (/^Request failed with status code \d+$/.test(error.message)) {
      return "Request failed";
    }
    return error.message.trim();
  }
  return "Request failed";
}

/**
 * Rejection shape used by every baseAxios caller.
 * Keeps `{ status, data }` and also sets `message` and `response` so existing pages can read either form.
 */
export function toApiError(error) {
  const status = error?.response?.status ?? error?.status ?? 0;
  const raw = error?.response?.data ?? error?.data;
  const message = apiErrorMessage(error);
  let data;
  if (isHtml(raw)) {
    data = raw;
  } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    data =
      typeof raw.message === "string" && raw.message.trim()
        ? raw
        : { ...raw, message };
  } else if (typeof raw === "string" && raw.trim()) {
    data = { message: raw.trim() };
  } else {
    data = { message };
  }
  const normalized = {
    status,
    data,
    message: typeof data === "object" && data?.message ? data.message : message,
  };
  if (status) {
    normalized.response = { status, data };
  }
  return normalized;
}

/** Retry safe reads after a dropped connection or a gateway timeout. Creates and updates are not retried. */
export function shouldRetryRequest(config, status) {
  if (!config) return false;
  const method = String(config.method || "get").toLowerCase();
  if (!IDEMPOTENT_METHODS.has(method)) return false;
  if (!status) return (config._networkRetry || 0) < MAX_NETWORK_RETRIES;
  if (GATEWAY_STATUSES.has(status)) return !config._gatewayRetry;
  return false;
}

export function markRetry(config, status) {
  if (!status) config._networkRetry = (config._networkRetry || 0) + 1;
  else config._gatewayRetry = true;
}

export function retryDelayMs(config) {
  return 1000 * (config?._networkRetry || 1);
}

/**
 * What to do after getUser().
 * "keep" leaves the current login in place. Only "signOut" drops the Cognito refresh token.
 */
export function sessionDecision(result) {
  if (result?.isOk) return "refresh";
  if (result?.reason === "auth") return "signOut";
  if (result?.reason === "transient") return "keep";
  return "clear";
}

/** Delay until the next silent refresh. An expired access token is retried, not treated as logout. */
export function nextRefreshDelay(expiryMs, now = Date.now()) {
  const timeUntilRefresh = expiryMs - now - REFRESH_BEFORE_MS;
  if (timeUntilRefresh <= 0) return MIN_RETRY_DELAY_MS;
  return timeUntilRefresh;
}

/** Backoff after a refresh call fails for a reason other than Cognito rejecting the refresh token. */
export function transientRetryDelay(attempt) {
  const index = Math.max(0, attempt);
  return TRANSIENT_RETRY_DELAYS_MS[
    Math.min(index, TRANSIENT_RETRY_DELAYS_MS.length - 1)
  ];
}

/** Keep the stored profile and, when Cognito already rotated tokens, use those. */
export function withRefreshedTokens(storedUser, tokens) {
  if (!storedUser) return null;
  if (!tokens?.accessToken && !tokens?.idToken) return storedUser;
  return {
    ...storedUser,
    ...(tokens.accessToken ? { accessToken: tokens.accessToken } : {}),
    ...(tokens.idToken ? { idToken: tokens.idToken } : {}),
  };
}

export function onAuthRejected(listener) {
  authRejectedListeners.add(listener);
  return () => authRejectedListeners.delete(listener);
}

export function notifyAuthRejected() {
  authRejectedListeners.forEach((listener) => {
    listener();
  });
}

function isHtml(value) {
  return typeof value === "string" && value.trimStart().startsWith("<");
}
